import type { ProcessArtifactDto } from '@api/processes';
import { useEditorContext } from '../useEditorContext';

/**
 * Resolves the artifact list to render for {@code nodeId} as a thin
 * overlay of the live run state on top of the persisted snapshot.
 *
 * <p>When a live run is tracked (running OR just completed), shows ONLY
 * the live artifacts for that run — never blends in persisted artifacts
 * from a previous run, which would visually duplicate items mid-run.
 * Falls back to the persisted snapshot when no live state exists
 * (e.g. first page load after a historic run).</p>
 *
 * <p>Extracted so {@link ArtifactsPanel} and the per-output artifact
 * list inside {@link OutputRow} both agree on which list to display;
 * otherwise the two surfaces could diverge during a run (e.g. live
 * artifacts under the outputs but stale persisted ones at the top).</p>
 */
export function useNodeLiveArtifacts(
  nodeId: string,
  latestArtifacts: readonly ProcessArtifactDto[] | undefined,
): readonly ProcessArtifactDto[] {
  const { runStateForNode } = useEditorContext();
  const liveState = runStateForNode(nodeId);
  if (liveState.source === 'live') {
    return liveState.live?.artifacts ?? [];
  }
  return latestArtifacts ?? [];
}
